import React from 'react';
import { observer } from 'mobx-react';
import { useCXRootStore } from '~/components/ClientExperience/stores/cx-root-store.provider';
import styled from 'styled-components';
import colors from '@honeybook/hbui/dist/styles/colors';
import HBAvatar from '@honeybook/hbui/dist/components/HBAvatar/HBAvatar';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import HBButton from '@honeybook/hbui/dist/components/HBButton/HBButton';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { mediumUp, upToMedium } from '@honeybook/hbui/dist/styles/breakpoints';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';
import { TPreviewTabId } from '~/types/builder.types';
import PreviewTypeTabs from '~/components/ClientExperience/OneFlow/components/FlowView/PreviewTypeTabs';
import InternationalAvailabilityModal from '~/components/ClientExperience/OneFlow/components/InternationalModal/InternationalAvailabilityModal';
import { Helmet } from 'react-helmet';
import { isCapturingThumbnailMode } from '~/components/ClientExperience/components/PublicTemplateThumbnails/utils';
import { usePublishedTemplateViewHeader } from '~/components/ClientExperience/OneFlow/components/FlowView/usePublishedTemplateViewHeader';
import { useBuildTestId } from '@honeybook/hbui/dist/components/common/useBuildTestId';
import is from 'styled-is';
import ArrowLineLeft24 from '@honeybook/icons/dist/icons/ArrowLineLeft24';
import { usePostMessageToParent } from '~/components/ClientExperience/PublicTemplateIframeEvents/usePostMessageToParent';
import useAnalytics from '~/common/useAnalytics';

interface Props {
    setDisplayType: (type: TPreviewTabId) => void;
    displayType: TPreviewTabId;
}

export const PUBLIC_TEMPLATE_UTM_SOURCE = 'publictemplate';

const OG_IMAGE =
    'https://res.cloudinary.com/honeybook/image/upload/f_auto,fl_lossy,q_auto/v1712150419/app/OG_templates.png';

const PublishedTemplateViewHeader = ({
    setDisplayType,
    displayType
}: Props) => {
    const {
        flowStore: { flow },
        flowViewUiStore: {
            isInIframePreview,
            shouldShowPreviewOfTestVersion,
            setIsInIframePreview,
            setShouldShowPreviewOfTestVersion,
            setPreviewDisplayType
        }
    } = useCXRootStore();
    const { isMediumUp } = useWindowSize();
    const { t } = useTranslation(NAMESPACES.CLIENT_EXPERIENCE, {
        keyPrefix: 'public_template'
    });
    const {
        clickUseTemplate,
        buttonTestIdName,
        buttonLabel,
        isInternationalModalOpen,
        setIsInternationalModalOpen,
        createdInProps,
        shouldShowReferralDiscount
    } = usePublishedTemplateViewHeader({ flow });

    const { postMessageToParent } = usePostMessageToParent();

    const { trackClick } = useAnalytics();

    const { is_gallery_template, referral_details } =
        flow?.public_configuration || {};
    const publisher = flow?.publisher;

    const containerTestId = useBuildTestId({
        componentName: 'PublishedTemplateViewHeader',
        identifier: 'published-template-view',
        suffix: 'header'
    });
    const avatarTestId = useBuildTestId({
        componentName: 'PublishedTemplateViewHeader',
        identifier: 'published-template-view',
        suffix: 'avatar'
    });
    const referralInfoTestId = useBuildTestId({
        componentName: 'referral-discount',
        identifier: 'published-template-view',
        suffix: 'referral'
    });

    const ogTags = (
        <Helmet>
            <meta property="og:title" content={t('og_details.title')} />
            <meta property="og:image" content={OG_IMAGE} />
        </Helmet>
    );

    const handleBackButtonClick = () => {
        trackClick('public template iframe back button');
        setIsInIframePreview(true);
        setShouldShowPreviewOfTestVersion(false);
        postMessageToParent({
            event: 'back_button_clicked'
        });
        setPreviewDisplayType('Desktop');
    };

    const referralDiscount = shouldShowReferralDiscount ? (
        <ReferralDiscountText
            color={shouldShowPreviewOfTestVersion ? 'neutral900' : 'white'}
            type={isMediumUp ? 'emphasis14' : 'emphasis12'}
            testId={referralInfoTestId}
        >
            {t(
                shouldShowPreviewOfTestVersion
                    ? 'referral_discount_test'
                    : 'referral_discount',
                {
                    amount: referral_details?.discount_amount,
                    duration: referral_details?.discount_duration
                }
            )}
        </ReferralDiscountText>
    ) : null;

    if (isCapturingThumbnailMode || isInIframePreview) {
        return null;
    }

    return (
        <TemplateHeader
            data-testid={containerTestId}
            shouldShowPreviewOfTestVersion={shouldShowPreviewOfTestVersion}
        >
            {ogTags}
            {shouldShowPreviewOfTestVersion && (
                <BackButton
                    testIdName="public-template-back"
                    size="sm"
                    customTheme={{
                        background: colors.neutral100,
                        hoverBackgroundColor: colors.neutral100,
                        color: colors.neutral900,
                        borderRadius: '4px'
                    }}
                    onClick={handleBackButtonClick}
                >
                    <ArrowLineLeft24 color={colors.neutral900} />
                </BackButton>
            )}
            {!shouldShowPreviewOfTestVersion && (
                <TemplateDetailsContainer>
                    <StyledHBAvatar
                        size="lg"
                        userInitials={publisher?.initials}
                        profileImageUrl={publisher?.profile_image_url}
                        testId={avatarTestId}
                    />
                    <TemplateDetails>
                        <TemplateName>
                            <TemplateTitle type="title16" color="white">
                                {flow.title}
                            </TemplateTitle>
                        </TemplateName>
                        <CreatedByContainer>
                            <HBText {...createdInProps}>
                                {t('created_in')}{' '}
                            </HBText>
                            {!is_gallery_template && (
                                <HBText type="caption12" color="neutral500">
                                    {t('created_by', {
                                        name: flow.publisher?.full_name
                                    })}
                                </HBText>
                            )}
                        </CreatedByContainer>
                        {!isMediumUp && referralDiscount}
                    </TemplateDetails>
                </TemplateDetailsContainer>
            )}
            {isMediumUp && (
                <PreviewTypeTabs
                    shouldShowPreviewOfTestVersion={
                        shouldShowPreviewOfTestVersion
                    }
                    onClickTab={setDisplayType}
                    selectedTabId={displayType}
                />
            )}
            <TemplateActionsContainer>
                {isMediumUp && referralDiscount}
                <HBButton
                    disabled={isInIframePreview === undefined}
                    onClick={clickUseTemplate}
                    testIdName={buttonTestIdName}
                    customTheme={{
                        background: colors.purpleHeart,
                        borderRadius: '4px'
                    }}
                    size="sm"
                >
                    {buttonLabel}
                </HBButton>
            </TemplateActionsContainer>
            <InternationalAvailabilityModal
                isOpen={isInternationalModalOpen}
                onClose={() => setIsInternationalModalOpen(false)}
            />
        </TemplateHeader>
    );
};

export default observer(PublishedTemplateViewHeader);

export const PUBLIC_TEMPLATE_HEADER_DESKTOP_HEIGHT_PX = 60;
export const PUBLIC_TEMPLATE_HEADER_MOBILE_HEIGHT_PX = 73;

const TemplateHeader = styled.div<{ shouldShowPreviewOfTestVersion: boolean }>`
    background-color: ${colors.neutral900};
    align-items: center;
    display: flex;
    padding: 0 18px;
    justify-content: space-between;
    position: sticky;
    z-index: 3;
    top: 0px;
    height: ${PUBLIC_TEMPLATE_HEADER_MOBILE_HEIGHT_PX}px;

    ${mediumUp} {
        height: ${PUBLIC_TEMPLATE_HEADER_DESKTOP_HEIGHT_PX}px;
    }
    ${is('shouldShowPreviewOfTestVersion')`
        background-color: ${colors.white};
        border-bottom: 1px solid ${colors.neutral200};
    `}
    & i {
        margin-right: 0px;
    }
`;

const BackButton = styled(HBButton)`
    height: 36px;
    width: 36px;
`;

const TemplateDetailsContainer = styled.div`
    gap: 8px;
    display: flex;
    align-items: flex-end;
    padding: 8px 0;

    ${mediumUp} {
        padding: 10px 0;
    }
`;

const TemplateName = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
`;

const TemplateDetails = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const TemplateActionsContainer = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
`;

const StyledHBAvatar = styled(HBAvatar)`
    border: none;
    ${upToMedium} {
        display: none;
    }
`;

const TemplateTitle = styled(HBText)`
    font-weight: 700;
`;

const CreatedByContainer = styled.div`
    line-height: 15px;
`;

const ReferralDiscountText = styled(HBText)`
    line-height: 20px;

    ${mediumUp} {
        line-height: 15px;
    }
`;
